import {useState} from 'react'
import styled, {keyframes} from 'styled-components'
import PDFModal from '../../../globalComponents/PDFModal'
import privFile from '../../../assets/files/Politica_cookie.pdf'
import logo from '../../../assets/licito-logo.svg'

const COOKIE_CONSENT_KEY = 'cookie_consent'

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`

const popIn = keyframes`
    from { transform: translateY(12px) scale(0.98); opacity: 0; }
    to { transform: translateY(0) scale(1); opacity: 1; }
`

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(10, 14, 30, 0.55);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9998;
    padding: 24px;
    animation: ${fadeIn} 0.2s ease-out;
`

const Card = styled.div`
    width: 100%;
    max-width: 620px;
    max-height: 90vh;
    overflow-y: auto;
    background-color: #ffffff;
    border-radius: 16px;
    padding: 32px 36px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
    animation: ${popIn} 0.25s ease-out;
`

const LogoRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 20px;

    img {
        height: 24px;
    }
`

const Title = styled.h2`
    text-align: center;
    font-size: 20px;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0 0 12px;
`

const Description = styled.p`
    text-align: center;
    font-size: 14px;
    line-height: 1.6;
    color: #5a5a6e;
    margin: 0 0 24px;
`

const CategoryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 20px;

    @media (min-width: 480px) {
        grid-template-columns: repeat(4, 1fr);
    }
`

const CategoryCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 6px;
    padding: 14px 10px;
    border-radius: 10px;
    background-color: #f6f7fb;
    border: 1px solid #ececf4;
`

const IconBadge = styled.div<{$bg: string}>`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    background-color: ${({$bg}) => $bg};
`

const CategoryTitle = styled.span`
    font-size: 12.5px;
    font-weight: 700;
    color: #1a1a2e;
`

const CategoryText = styled.span`
    font-size: 11px;
    line-height: 1.4;
    color: #7a7a8e;
`

const InfoBox = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: #ecfdf5;
    border: 1px solid #bbf7d0;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 12.5px;
    color: #16613f;
    margin-bottom: 20px;
`

const PolicyLink = styled.span`
    color: #3394ef;
    cursor: pointer;
    font-weight: 600;
    text-decoration: underline;

    &:hover {
        opacity: 0.8;
    }
`

const AcceptButton = styled.button`
    width: 100%;
    background: linear-gradient(90deg, #3ffe81, #3394ef);
    color: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 12px 20px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        opacity: 0.9;
    }
`

const ManageLink = styled.button`
    display: block;
    margin: 14px auto 0;
    background: none;
    border: none;
    color: #3394ef;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
`

const WarningBox = styled.div`
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 20px;
`

const WarningText = styled.p`
    color: #b91c1c;
    font-size: 13px;
    line-height: 1.5;
    margin: 0;
`

interface Category {
    key: string
    icon: string
    iconBg: string
    title: string
    text: string
}

const CATEGORIES: Category[] = [
    {
        key: 'necessary',
        icon: '🛡️',
        iconBg: '#d3f8e2',
        title: 'Necessários',
        text: 'Essenciais para o funcionamento do site.',
    },
    {
        key: 'performance',
        icon: '📊',
        iconBg: '#dceefe',
        title: 'Desempenho',
        text: 'Garantem a estabilidade e o desempenho do sistema.',
    },
    {
        key: 'functional',
        icon: '🎯',
        iconBg: '#e6e0fb',
        title: 'Funcionais',
        text: 'Mantêm sua sessão e preferências de uso.',
    },
    {
        key: 'security',
        icon: '🔐',
        iconBg: '#fde2e2',
        title: 'Segurança',
        text: 'Protegem contra fraudes e acessos indevidos.',
    },
]

export function hasCookieConsent(): boolean {
    return localStorage.getItem(COOKIE_CONSENT_KEY) === 'accepted'
}

export default function CookieBanner() {
    const [visible, setVisible] = useState(!hasCookieConsent())
    const [refused, setRefused] = useState(false)
    const [pdf, setPdf] = useState<any>(null)

    if (!visible) return null

    const handleAccept = () => {
        localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted')
        setVisible(false)
    }

    const handleRefuse = () => {
        setRefused(true)
    }

    return (
        <>
            <Overlay>
                <Card>
                    <LogoRow>
                        <img src={logo} alt="licito.guru" />
                    </LogoRow>

                    {!refused ? (
                        <>
                            <Title>Este site usa cookies</Title>
                            <Description>
                                Utilizamos cookies necessários para manter sua
                                sessão ativa e garantir o funcionamento do
                                sistema. Você precisa aceitá-los para continuar
                                utilizando a plataforma.
                            </Description>

                            <CategoryGrid>
                                {CATEGORIES.map((category) => (
                                    <CategoryCard key={category.key}>
                                        <IconBadge $bg={category.iconBg}>
                                            {category.icon}
                                        </IconBadge>
                                        <CategoryTitle>
                                            {category.title}
                                        </CategoryTitle>
                                        <CategoryText>
                                            {category.text}
                                        </CategoryText>
                                    </CategoryCard>
                                ))}
                            </CategoryGrid>

                            <InfoBox>
                                🔒 Seus dados estão seguros conosco. Consulte
                                nossa{' '}
                                <PolicyLink onClick={() => setPdf(privFile)}>
                                    Política de Privacidade
                                </PolicyLink>
                                .
                            </InfoBox>

                            <AcceptButton onClick={handleAccept}>
                                ✓ Aceitar cookies e continuar
                            </AcceptButton>

                            <ManageLink onClick={handleRefuse}>
                                Recusar
                            </ManageLink>
                        </>
                    ) : (
                        <>
                            <Title>Cookies são necessários</Title>
                            <WarningBox>
                                <WarningText>
                                    ⚠️ Sem aceitar os cookies, o sistema não
                                    funcionará corretamente. Utilizamos cookies
                                    para manter sua sessão de acesso ativa. Você
                                    precisará aceitá-los para utilizar a
                                    plataforma.
                                </WarningText>
                            </WarningBox>
                            <AcceptButton onClick={handleAccept}>
                                ✓ Aceitar cookies e continuar
                            </AcceptButton>
                            <ManageLink onClick={() => setRefused(false)}>
                                Voltar
                            </ManageLink>
                        </>
                    )}
                </Card>
            </Overlay>
            {!!pdf && <PDFModal item={pdf} setModal={setPdf} />}
        </>
    )
}
